import { QueryObserverResult } from '@tanstack/react-query';
import { AxiosError, AxiosResponse } from 'axios';
import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useGetLoginUserInfo } from '../../../../api/auth/auth';
import {
  useGetConfirmationRequestDetail,
  useGetConfirmationRequestsList,
  usePostConfirmationRequestCancel,
  usePostConfirmationRequestLock,
  usePostConfirmationRequsestUpdate,
} from '../../../../api/confirmation-requests/confirmation-requests';
import {
  ConfirmationRequestCategory,
  ConfirmationRequestDivision,
  ConfirmationRequestListItem,
} from '../../../../api/model';
import POST_MESSAGES from '../../../../constants/postMessages';
import { useDefaultOptions } from '../../../../hooks/api';
import usePostMessage from '../../../../hooks/usePostMessage';
import { postWithKeepAlive } from '../../../../utils/api';
import ErrorComponent from '../../../atoms/ErrorComponent';
import WaitComponent from '../../../atoms/WaitComponent';
import AnotherTabLockingErrorModal from '../../../modules/AnotherTabLockingErrorModal';
import ErrorModal from '../../../modules/ErrorModal';
import SlideToggle from '../../../modules/SlideToggle';
import SpinnerModal from '../../../modules/SpinnerModal';
import { ConfirmationRequestCategories, ConfirmationRequestStatuses } from './ProductionConfirmationRequestsTab';

export interface ProductionConfirmationRequestListHandler {
  refresh: () => void;
}

interface Props {
  productionId: number;
  editModeTabIndex: number | undefined;
  setEditable: (editing: boolean) => void;
}

/**
 * 確認依頼一覧画面
 */
const ProductionConfirmationRequestList = forwardRef<ProductionConfirmationRequestListHandler, Props>(
  ({ productionId, editModeTabIndex, setEditable }: Props, ref) => {
    const {
      t,
      i18n: { language },
    } = useTranslation();

    // ロック処理定義
    const options = useDefaultOptions(language);

    const { mutateAsync: confirmationRequestLockAsync, error: confirmationRequestLockError } =
      usePostConfirmationRequestLock(options);

    const [displayConfirmationRequestLockError, setDisplayConfirmationRequestLockError] = useState(false);
    const [displaySpinnerModal, setDisplaySpinnerModal] = useState(false);

    useEffect(() => {
      setDisplayConfirmationRequestLockError(confirmationRequestLockError != null);
      setDisplaySpinnerModal(false);
    }, [confirmationRequestLockError]);

    // ロックキャンセル処理定義
    const { mutateAsync: confirmationRequestCancelAsync, error: confirmationRequestCancelError } =
      usePostConfirmationRequestCancel(options);

    const [displayConfirmationRequestCancelError, setDisplayConfirmationRequestCancelError] = useState(false);

    useEffect(
      () => setDisplayConfirmationRequestCancelError(confirmationRequestCancelError != null),
      [confirmationRequestCancelError]
    );

    // 一覧取得
    const {
      data: getConfirmationRequestsListResponse,
      isError,
      error,
      isLoading,
      refetch: refetchConfirmationRequestsList,
    } = useGetConfirmationRequestsList(productionId, options);

    const [toggleFlags, setToggleFlags] = useState<{ categoryId: number; isOpen: boolean }[]>();
    const [confirmationRequestCategories, setConfirmationRequestCategories] = useState<ConfirmationRequestCategory[]>();
    const [confirmationRequestDivisions, setConfirmationRequestDivisions] = useState<ConfirmationRequestDivision[]>();
    const [confirmationRequestListItems, setConfirmationRequestListItems] = useState<ConfirmationRequestListItem[]>();

    useEffect(() => {
      setToggleFlags(
        getConfirmationRequestsListResponse?.data.defaultConfirmationRequestCategoryIds?.map((x) => ({
          categoryId: x,
          isOpen: false,
        }))
      );
      setConfirmationRequestCategories(getConfirmationRequestsListResponse?.data.confirmationRequestCategories);
      setConfirmationRequestDivisions(getConfirmationRequestsListResponse?.data.confirmationRequestDivisions);
      setConfirmationRequestListItems(getConfirmationRequestsListResponse?.data.confirmationRequestListItems);
    }, [
      getConfirmationRequestsListResponse?.data.confirmationRequestCategories,
      getConfirmationRequestsListResponse?.data.confirmationRequestDivisions,
      getConfirmationRequestsListResponse?.data.confirmationRequestListItems,
      getConfirmationRequestsListResponse?.data.defaultConfirmationRequestCategoryIds,
    ]);

    // 更新処理の公開
    useImperativeHandle(
      ref,
      () => ({
        refresh: () => {
          void refetchConfirmationRequestsList();
        },
      }),
      [refetchConfirmationRequestsList]
    );

    // 編集状態ステータス
    const [isInEdit, setIsInEdit] = useState(false);
    const setEditMode = useCallback(
      (state: boolean) => {
        setEditable(state);
        setIsInEdit(state);
      },
      [setEditable]
    );

    // ロック処理

    // ロックを取得する setInterval の返す ID
    const refLockIntervalId = useRef<NodeJS.Timer>();
    useEffect(() => () => clearInterval(refLockIntervalId.current), []);

    // beforeUnloadEvent（ブラウザを閉じようとしたときに、メッセージ表示）
    const beforeUnload = useCallback((e: BeforeUnloadEvent) => {
      e.returnValue = '';
    }, []);

    // コンポーネントが表示されなくなる時に、beforeunload イベントの解除
    useEffect(() => () => window.removeEventListener('beforeunload', beforeUnload), [beforeUnload]);

    // unload 関数（ブラウザを閉じるときに発生して、ロック解除するためのもの）
    const unload = useCallback(async () => {
      await postWithKeepAlive(`/confirmationRequests/cancel/${productionId}`, language);
    }, [language, productionId]);

    const refUnload = useRef<() => Promise<void>>();

    // コンポーネントが表示されなくなる時に、unload イベントの実行と解除
    useEffect(
      () => () => {
        if (refUnload.current != null) {
          void refUnload.current().then(() => {
            if (refUnload.current != null) {
              window.removeEventListener('unload', refUnload.current);
            }
          });
        }
      },
      []
    );

    // 編集をやめる
    const onFinishEditing = useCallback(() => {
      // 編集モードをやめる
      setEditable(false);

      // ロックの定期実行をやめる
      clearInterval(refLockIntervalId.current);

      // ブラウザを閉じた時などのイベントを解除
      window.removeEventListener('beforeunload', beforeUnload);
      if (refUnload.current != null) {
        window.removeEventListener('unload', refUnload.current);
      }

      // スピナーモーダルを非表示
      setDisplaySpinnerModal(false);
    }, [beforeUnload, setEditable]);

    const [displayAnotherTabLockError, setDisplayAnotherTabLockError] = useState(false);

    // 編集モードにする
    const onEditClicked = useCallback(async () => {
      // 自身が編集モードのときは何もしない
      if (editModeTabIndex === 4) return;

      // 編集モードのタブがある場合はエラー表示
      if (editModeTabIndex != null) {
        setDisplayAnotherTabLockError(true);
        return;
      }

      setDisplaySpinnerModal(true);

      const onSuccess = async () => {
        try {
          await refetchConfirmationRequestsList();

          // 定期的にロック API をたたく
          const lockInterval = parseInt(process.env.REACT_APP_GET_LOCK_INTERVAL ?? '10000', 10);
          refLockIntervalId.current = setInterval(() => {
            void confirmationRequestLockAsync({ productionId }, { onError: onFinishEditing });
          }, lockInterval);

          // ブラウザを閉じた時などのロック解除を設定
          window.addEventListener('beforeunload', beforeUnload);
          refUnload.current = unload;
          window.addEventListener('unload', refUnload.current);

          setEditMode(true);
        } catch {
          // Do nothing
        } finally {
          setDisplaySpinnerModal(false);
        }
      };

      const onError = () => setDisplaySpinnerModal(false);

      await confirmationRequestLockAsync({ productionId }, { onSuccess, onError });
    }, [
      editModeTabIndex,
      confirmationRequestLockAsync,
      productionId,
      refetchConfirmationRequestsList,
      beforeUnload,
      unload,
      setEditMode,
      onFinishEditing,
    ]);

    // ロックキャンセル処理
    const onCancelClicked = useCallback(async () => {
      setDisplaySpinnerModal(true);

      await confirmationRequestCancelAsync({ productionId });

      // ロック API の呼び出しを解除
      clearInterval(refLockIntervalId.current);

      setDisplaySpinnerModal(false);

      setEditMode(false);
    }, [productionId, confirmationRequestCancelAsync, setEditMode]);

    // 編集画面からの更新通知受信
    usePostMessage(POST_MESSAGES.ReloadConfirmationRequestList, async () => {
      setDisplaySpinnerModal(true);

      await refetchConfirmationRequestsList();
      await onCancelClicked();

      setDisplaySpinnerModal(false);
    });

    // 大項目のトグル処理
    const reverseToggleFlag = useCallback((confirmationRequestCategoryId: number) => {
      setToggleFlags((preToggleFlags) => {
        const toggleFlag = preToggleFlags?.find((x) => x.categoryId === confirmationRequestCategoryId);
        if (preToggleFlags != null && toggleFlag != null) {
          toggleFlag.isOpen = !toggleFlag.isOpen;
          return [...preToggleFlags];
        }

        return preToggleFlags?.concat({ categoryId: confirmationRequestCategoryId, isOpen: true });
      });
    }, []);

    // ログイン中のユーザー情報取得
    const loginUserInfoResponse = useGetLoginUserInfo(options);
    const loginUser = loginUserInfoResponse.data?.data;

    if (isLoading) {
      return <WaitComponent />;
    }
    if (isError) {
      return <ErrorComponent error={error} />;
    }

    // 原稿URL ベースパス
    const basePath = window.location.origin + (process.env.REACT_APP_ROUTER_BASEPATH || '');

    return (
      <>
        <div className="master-navi">
          <ul>
            <li className="master-navibtn3">
              <button type="button" className="text-button" onClick={() => onEditClicked()} disabled={isInEdit}>
                <i className="fa-solid fa-pen-to-square" />
                {t('編集')}
              </button>
            </li>
            <li className="master-navibtn4">
              <button type="button" className="text-button" onClick={() => onCancelClicked()} disabled={!isInEdit}>
                <i className="fa-solid fa-circle-xmark" />
                {t('キャンセル')}
              </button>
            </li>
          </ul>
        </div>
        {confirmationRequestCategories?.map((category) => {
          const divisions = confirmationRequestDivisions?.filter(
            (division) => division.confirmation_request_category_id === category.confirmation_request_category_id
          );
          const isOpen = toggleFlags?.find((x) => x.categoryId === category.confirmation_request_category_id)?.isOpen;
          return (
            <div key={category.confirmation_request_category_id}>
              <h3 className="mdh2">
                <button
                  type="button"
                  className="text-button"
                  onClick={() => reverseToggleFlag(category.confirmation_request_category_id ?? 0)}
                >
                  <span>
                    {category.confirmation_request_category_name}
                    <span className="num">{divisions?.length}</span>
                    <i className={`fa-solid ${isOpen ? 'fa-chevron-down' : 'fa-chevron-up'}`} />
                  </span>
                </button>
              </h3>
              <SlideToggle open={isOpen ?? false}>
                <div id="master1">
                  {divisions?.map((division) => (
                    <div key={division.confirmation_request_division_id}>
                      <h4 className="mdh1-4">{division.confirmation_request_division_name}</h4>
                      <p className="newadd">
                        {isInEdit && (
                          <Link
                            to={`/${language}/productions/edit/${productionId}/confirmation-request/add/${
                              division.confirmation_request_division_id ?? 0
                            }`}
                            target="confirmation-request"
                          >
                            <i className="fa-solid fa-circle-plus" />
                            {t('新規追加')}
                          </Link>
                        )}
                      </p>
                      <div className="scroll">
                        <table className="table11">
                          <tbody>
                            <tr>
                              <th>{t('記載章')}</th>
                              <th>{t('件名')}</th>
                              <th>{t('原稿URL')}</th>
                              <th>{t('送信者')}</th>
                              <th>{t('宛先')}</th>
                              <th>{t('最終更新日')}</th>
                              <th>{t('更新者')}</th>
                              <th>{t('回答期限')}</th>
                              <th> </th>
                            </tr>
                            {confirmationRequestListItems
                              ?.filter(
                                (listItem) =>
                                  listItem.confirmation_request_division_id ===
                                  division.confirmation_request_division_id
                              )
                              .map((listItem) => (
                                <tr key={listItem.confirmation_request_id}>
                                  <td className="detailbtn1-4b">
                                    <Link
                                      to={`/${language}/productions/edit/${productionId}/confirmation-request/${
                                        listItem.confirmation_request_status === ConfirmationRequestStatuses.Completed
                                          ? 'edit'
                                          : 'edit'
                                      }/${listItem.confirmation_request_id ?? ''}`}
                                      target="confirmation-request"
                                    >
                                      {!listItem.confirmation_request_chapter_name
                                        ? t('（記載章無し）')
                                        : listItem.confirmation_request_chapter_name}
                                    </Link>
                                  </td>
                                  <td>{listItem.title}</td>
                                  <td>
                                    {listItem.url != null && (
                                      <Link
                                        to={`/${language}/productions/sections/${productionId}/ps?section_id=${listItem.url}`}
                                        target="_blank"
                                      >
                                        {`${basePath}/${language}/productions/sections/${productionId}/ps?section_id=${listItem.url}`}
                                      </Link>
                                    )}
                                  </td>
                                  <td>{listItem.request_user_name}</td>
                                  <td>
                                    {listItem.address_names?.map((addItem, addKey) => (
                                      <p key={`${String(addKey)}-${String(listItem.confirmation_request_id)}`}>
                                        {addItem}
                                      </p>
                                    ))}
                                  </td>
                                  <td>{listItem.updated_on}</td>
                                  <td>{listItem.update_user_name}</td>
                                  <td>{listItem.due_on}</td>
                                  <td>
                                    {listItem.confirmation_request_id != null &&
                                      (() => {
                                        if (
                                          (listItem.confirmation_request_status ===
                                            ConfirmationRequestStatuses.Replied ||
                                            (listItem.confirmation_request_status === ConfirmationRequestStatuses.New &&
                                              category.confirmation_request_category_id ===
                                                ConfirmationRequestCategories.System)) &&
                                          (listItem?.request_user_id === loginUser?.user_id ||
                                            listItem?.request_user_id == null)
                                        ) {
                                          return (
                                            <ConfirmationRequestStatus
                                              confirmationRequestId={listItem.confirmation_request_id}
                                              refetch={refetchConfirmationRequestsList}
                                            />
                                          );
                                        }
                                        if (
                                          listItem.confirmation_request_status === ConfirmationRequestStatuses.Replied
                                        ) {
                                          return (
                                            <>
                                              <i className="fa-solid fa-circle-check" />
                                              {t('完了')}
                                            </>
                                          );
                                        }
                                        if (
                                          listItem.confirmation_request_status === ConfirmationRequestStatuses.Completed
                                        ) {
                                          return (
                                            <>
                                              <i className="fa-solid fa-circle-check confirmation-request-completed" />
                                              {t('完了')}
                                            </>
                                          );
                                        }
                                        return undefined;
                                      })()}
                                  </td>
                                </tr>
                              ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  ))}
                </div>
              </SlideToggle>
            </div>
          );
        })}
        {displaySpinnerModal && <SpinnerModal />}
        {displayConfirmationRequestLockError && (
          <ErrorModal
            error={confirmationRequestLockError}
            onClickOk={() => {
              setDisplaySpinnerModal(false);
              setDisplayConfirmationRequestLockError(false);
            }}
          />
        )}
        {displayConfirmationRequestCancelError && (
          <ErrorModal
            error={confirmationRequestCancelError}
            onClickOk={() => {
              setDisplaySpinnerModal(false);
              setDisplayConfirmationRequestCancelError(false);
            }}
          />
        )}
        {displayAnotherTabLockError && editModeTabIndex != null && (
          <AnotherTabLockingErrorModal
            editModeTabIndex={editModeTabIndex}
            onClickOk={() => setDisplayAnotherTabLockError(false)}
          />
        )}
      </>
    );
  }
);

export default ProductionConfirmationRequestList;

interface ConfirmationRequestStatusProps {
  confirmationRequestId: number;
  refetch: () => Promise<QueryObserverResult<AxiosResponse, AxiosError>>;
}

/**
 * 完了状態表示，設定コンポーネント
 * @param param0
 * @returns
 */
function ConfirmationRequestStatus({ confirmationRequestId, refetch }: ConfirmationRequestStatusProps) {
  const {
    t,
    i18n: { language },
  } = useTranslation();

  // 完了処理定義
  const options = useDefaultOptions(language);

  const { mutateAsync: confirmationRequsestUpdateAsync, error: confirmationRequsestUpdateError } =
    usePostConfirmationRequsestUpdate(options);

  const [displayConfirmationRequsestUpdateError, setDisplayConfirmationRequsestUpdateError] = useState(false);

  useEffect(
    () => setDisplayConfirmationRequsestUpdateError(confirmationRequsestUpdateError != null),
    [confirmationRequsestUpdateError]
  );

  // 詳細取得処理定義
  const { refetch: refetchConfirmationRequestDetail, error: getConfirmationRequestDetailError } =
    useGetConfirmationRequestDetail(confirmationRequestId, { ...options, query: { enabled: false } });

  const [displayGetConfirmationRequestDetailError, setDisplayGetConfirmationRequestDetailError] = useState(false);

  useEffect(
    () => setDisplayGetConfirmationRequestDetailError(getConfirmationRequestDetailError != null),
    [getConfirmationRequestDetailError]
  );

  // 完了処理
  const [displaySpinnerModal, setDisplaySpinnerModal] = useState(false);

  const onCompleteClicked = useCallback(async () => {
    setDisplaySpinnerModal(true);

    const response = await refetchConfirmationRequestDetail();

    if (!response.isError) {
      await confirmationRequsestUpdateAsync({
        confirmationRequestId,
        data: {
          ...response.data?.data,
          action: ConfirmationRequestStatuses.Completed,
          title: response.data?.data.title ?? undefined,
          url: response.data?.data.url ?? undefined,
          judge: response.data?.data.judge ?? undefined,
          due_on: response.data?.data.due_on ?? undefined,
          comment: response.data?.data.comment ?? undefined,
          confirmation_user_ids:
            response.data?.data?.confirmation_users
              ?.filter((x) => x.user_id != null)
              ?.map((x) => x.user_id)
              ?.join(',') ?? undefined,
          confirmation_team_ids:
            response.data?.data?.confirmation_teams
              ?.filter((x) => x.team_id != null)
              ?.map((x) => x.team_id)
              ?.join(',') ?? undefined,
          base_history_id: undefined,
          save_revision: true, // 小分類区分やアクションやurlを見る必要があるが、バックエンドでチェックする
        },
      });

      await refetch();
    }

    setDisplaySpinnerModal(false);
  }, [confirmationRequestId, refetchConfirmationRequestDetail, confirmationRequsestUpdateAsync, refetch]);

  return (
    <>
      <div>
        <button type="button" className="text-button" onClick={() => onCompleteClicked()}>
          <i className="fa-solid fa-circle-check" />
          {t('完了')}
        </button>
      </div>
      {displaySpinnerModal && <SpinnerModal />}
      {displayGetConfirmationRequestDetailError && (
        <ErrorModal
          error={getConfirmationRequestDetailError}
          onClickOk={() => {
            setDisplaySpinnerModal(false);
            setDisplayGetConfirmationRequestDetailError(false);
          }}
        />
      )}
      {displayConfirmationRequsestUpdateError && (
        <ErrorModal
          error={confirmationRequsestUpdateError}
          onClickOk={() => {
            setDisplaySpinnerModal(false);
            setDisplayConfirmationRequsestUpdateError(false);
          }}
        />
      )}
    </>
  );
}
